/*
 * Klaviyo + Weglot: send product events in the visitor's language.
 *
 * - KlaviyoLang.getLang(), localizeUrl(), getLocalizedTitle(): shared helpers.
 * - Profile properties "lang" and "Language" for identified profiles and on form submit.
 * - "Added to Cart" after every successful /cart/add(.js) request (one event per added line).
 * - "Viewed Product Localized" on product pages (the app embed keeps sending "Viewed Product").
 *
 * Titles are read from the DOM that Weglot has already translated; no Weglot API calls.
 * Config comes from window.klaviyoLangConfig (layout/theme.liquid).
 */
(function () {
  if (window.KlaviyoLang) return;

  var LANGS = ['de', 'fr', 'it', 'en'];
  var BASE_URL = 'https://switcher.com';
  var TITLE_SELECTOR = '.main_product_title, .card__heading a, a.cart-item__name';
  var CONTEXT_MAX_AGE = 15000;
  var config = window.klaviyoLangConfig || {};
  var originalFetch = window.fetch;

  function normalizeLang(code) {
    if (!code) return null;
    code = String(code).toLowerCase().slice(0, 2);
    return LANGS.indexOf(code) !== -1 ? code : null;
  }

  function isWeglotReady() {
    return !!(window.Weglot && window.Weglot.initialized && typeof window.Weglot.getCurrentLang === 'function');
  }

  // Calls back once Weglot reports "initialized", or after WEGLOT_TIMEOUT so events are never lost.
  var WEGLOT_TIMEOUT = 3000;
  var weglotCallbacks = [];
  var weglotDone = false;
  function flushWeglot() {
    if (weglotDone) return;
    weglotDone = true;
    weglotCallbacks.splice(0).forEach(function (cb) {
      try { cb(); } catch (e) { console.error('KlaviyoLang', e); }
    });
  }
  function whenWeglotReady(cb) {
    if (weglotDone) { cb(); return; }
    weglotCallbacks.push(cb);
  }
  (function waitForWeglot() {
    var started = Date.now();
    var listening = false;
    (function poll() {
      if (isWeglotReady()) { flushWeglot(); return; }
      if (!listening && window.Weglot && typeof window.Weglot.on === 'function') {
        listening = true;
        window.Weglot.on('initialized', flushWeglot);
      }
      if (Date.now() - started > WEGLOT_TIMEOUT) { flushWeglot(); return; }
      setTimeout(poll, 100);
    })();
  })();

  function getLang() {
    if (isWeglotReady()) {
      try {
        var weglotLang = normalizeLang(window.Weglot.getCurrentLang());
        if (weglotLang) return weglotLang;
      } catch (e) {}
    }
    var match = window.location.pathname.match(/^\/(fr|it|en)(\/|$)/);
    if (match) return match[1];
    return normalizeLang(document.documentElement.getAttribute('lang')) || 'de';
  }

  // "de" keeps the URL as it is (made absolute); other languages get https://switcher.com/{lang}/...
  function localizeUrl(url, lang) {
    if (!url) return url;
    var parsed;
    try { parsed = new URL(url, BASE_URL); } catch (e) { return url; }
    var path = parsed.pathname.replace(/^\/(fr|it|en)(?=\/|$)/, '') || '/';
    lang = normalizeLang(lang) || 'de';
    if (lang === 'de') return /^https?:\/\//.test(url) ? url : BASE_URL + path + parsed.search + parsed.hash;
    return BASE_URL + '/' + lang + path + parsed.search + parsed.hash;
  }

  function textOf(el) {
    return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function handleFromUrl(url) {
    // Weglot writes "/it/Products/..." for Italian.
    var match = url && String(url).match(/\/products\/([^/?#]+)/i);
    if (!match) return null;
    try { return decodeURIComponent(match[1]); } catch (e) { return match[1]; }
  }

  function findTitleElement(handle) {
    var pdpProduct = config.product;
    if (!handle || (pdpProduct && handle === pdpProduct.handle)) {
      var pdpTitle = document.querySelector('.main_product_title');
      if (pdpTitle) return pdpTitle;
    }
    if (!handle) return null;
    var links = document.querySelectorAll('.card__heading a[href*="/products/" i], a.cart-item__name[href*="/products/" i]');
    for (var i = 0; i < links.length; i++) {
      if (handleFromUrl(links[i].getAttribute('href')) === handle && textOf(links[i])) return links[i];
    }
    return null;
  }

  // Translated product title from the DOM; falls back to the given (Liquid/German) title.
  function getLocalizedTitle(fallback, handle) {
    return textOf(findTitleElement(handle)) || fallback;
  }

  function track(eventName, properties) {
    window._learnq = window._learnq || [];
    window._learnq.push(['track', eventName, properties]);
  }

  function formatPrice(cents) {
    var amount = (Number(cents || 0) / 100).toFixed(2);
    // Same output as Liquid's money filter (e.g. "39.00 "), like the app embed's item.Price.
    var template = config.moneyTemplate;
    return template && template.indexOf('1.00') !== -1 ? template.replace('1.00', amount) : amount;
  }

  /* ---------- Profile language ---------- */

  function languageProperties(lang) {
    lang = normalizeLang(lang) || getLang();
    return { lang: lang, Language: lang };
  }

  function withKlaviyo(cb) {
    var started = Date.now();
    (function poll() {
      var klaviyo = window.klaviyo;
      if (klaviyo && typeof klaviyo.identify === 'function' && typeof klaviyo.isIdentified === 'function') {
        cb(klaviyo);
        return;
      }
      if (Date.now() - started < 20000) setTimeout(poll, 250);
    })();
  }

  function identifyLanguageIfKnown(lang) {
    withKlaviyo(function (klaviyo) {
      Promise.resolve(klaviyo.isIdentified())
        .then(function (identified) {
          if (identified) return klaviyo.identify(languageProperties(lang));
        })
        .catch(function () {});
    });
  }

  whenWeglotReady(function () {
    identifyLanguageIfKnown(getLang());
    if (window.Weglot && typeof window.Weglot.on === 'function') {
      window.Weglot.on('languageChanged', function (newLang) {
        identifyLanguageIfKnown(newLang);
      });
    }
  });

  window.addEventListener('klaviyoForms', function (e) {
    if (!e.detail || (e.detail.type !== 'submit' && e.detail.type !== 'stepSubmit')) return;
    var properties = languageProperties(getLang());
    if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
      window.klaviyo.identify(properties);
    } else {
      window._learnq = window._learnq || [];
      window._learnq.push(['identify', properties]);
    }
  });

  /* ---------- Added to Cart ---------- */

  // The title is read when the shopper clicks/submits: quick-add modals empty
  // themselves as soon as the cart response arrives.
  var lastContext = null;

  function captureContext(target) {
    if (!target || !target.closest) return;
    var titleEl = null;
    // Quick-add modals live outside the product card: use the card of the button that opened it.
    var modal = target.closest('quick-add-modal');
    if (modal) {
      titleEl = modal.querySelector(TITLE_SELECTOR);
      var openerCard = !titleEl && modal.openedBy && modal.openedBy.closest('.card-wrapper, .card');
      if (openerCard) titleEl = openerCard.querySelector(TITLE_SELECTOR);
    } else {
      for (var node = target; node && node !== document.body && !titleEl; node = node.parentElement) {
        titleEl = node.querySelector && node.querySelector(TITLE_SELECTOR);
      }
    }
    var title = textOf(titleEl);
    var handle = null;
    if (titleEl) {
      var link = titleEl.closest('a[href]') ||
        (titleEl.closest('.product__title') && titleEl.closest('.product__title').querySelector('a[href*="/products/" i]'));
      handle = link ? handleFromUrl(link.getAttribute('href')) : null;
    }
    if (!handle && modal && modal.openedBy) handle = handleFromUrl(modal.openedBy.getAttribute('data-product-url'));
    if (!handle && !modal && config.product && target.closest('product-info')) handle = config.product.handle;
    lastContext = { title: title, handle: handle, time: Date.now() };
  }

  document.addEventListener('submit', function (e) {
    captureContext(e.submitter || e.target);
  }, true);
  document.addEventListener('click', function (e) {
    var target = e.target && e.target.closest && e.target.closest('button, [type="submit"], [role="button"]');
    if (target) captureContext(target);
  }, true);

  // Compare-at prices and collection titles are not part of the /cart/add.js answer.
  var productDataCache = {};
  if (config.product) {
    productDataCache[config.product.handle] = Promise.resolve({
      categories: config.product.categories,
      compareAt: config.product.compareAt
    });
  }
  function getProductData(handle) {
    if (!handle) return Promise.resolve(null);
    if (!productDataCache[handle]) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      productDataCache[handle] = originalFetch.call(window, root + 'products/' + encodeURIComponent(handle) + '?view=klaviyo-data', {
        credentials: 'same-origin'
      })
        .then(function (res) { return res.ok ? res.text() : null; })
        .then(function (text) { return text ? JSON.parse(text) : null; })
        .catch(function () { return null; });
    }
    return productDataCache[handle];
  }

  function addedItemsFrom(data) {
    if (!data || data.status) return [];
    if (Array.isArray(data.items)) return data.items;
    if (data.variant_id) return [data];
    return [];
  }

  // Private properties that bundle apps set on lines the shopper chose themselves
  // (Fly Bundles quantity breaks, e.g. the preselected 3-pack: "_flyBundles:OfferId").
  var BUNDLE_PROPERTY_PREFIXES = ['_flyBundles:'];
  function isBundleProperty(key) {
    return BUNDLE_PROPERTY_PREFIXES.some(function (prefix) { return key.indexOf(prefix) === 0; });
  }

  // Free lines and lines added by apps (free gifts, bundle components) are not shopper adds:
  // price or final price 0, a private property ("_..."; Shopify's own "__shopify_..." gift-card
  // fields and bundle-app properties excluded) or a property name containing "gift" on a
  // product that is not a gift card.
  function isShopperAdd(item) {
    var price = Number(item.price);
    var finalPrice = item.final_price == null ? price : Number(item.final_price);
    if (!price || !finalPrice) return false;
    var properties = item.properties || {};
    for (var key in properties) {
      if (!Object.prototype.hasOwnProperty.call(properties, key)) continue;
      if (properties[key] === null || properties[key] === '') continue;
      if (key.indexOf('__shopify_') === 0 || isBundleProperty(key)) continue;
      if (key.charAt(0) === '_') return false;
      if (!item.gift_card && /gift/i.test(key)) return false;
    }
    return true;
  }

  function trackAddedItem(item, context, addedQuantity) {
    getProductData(item.handle).then(function (productData) {
      whenWeglotReady(function () {
        var lang = getLang();
        var fallbackTitle = item.product_title || item.title;
        var name = context && context.title && context.handle === item.handle
          ? context.title
          : getLocalizedTitle(fallbackTitle, item.handle);
        var compareAt = productData && productData.compareAt ? productData.compareAt[item.variant_id] : 0;
        var image = item.image || (item.featured_image && item.featured_image.url) || '';
        track('Added to Cart', {
          Name: name,
          ProductID: item.product_id,
          Categories: (productData && productData.categories) || [],
          ImageURL: image.indexOf('//') === 0 ? 'https:' + image : image,
          URL: localizeUrl(item.url, lang),
          Brand: item.vendor,
          Price: formatPrice(item.price),
          Value: (Number(item.price || 0) / 100).toFixed(2),
          CompareAtPrice: formatPrice(compareAt),
          Quantity: addedQuantity || Number(item.quantity) || 1,
          Language: lang
        });
      });
    });
  }

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return input ? String(input) : '';
  }

  function requestMethod(input, init) {
    return String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
  }

  // /cart/add.js answers with the line's total quantity in the cart, so the added
  // quantity per variant id is read from the request body (FormData, URL-encoded or JSON).
  function requestedQuantities(init) {
    var body = init && init.body;
    var entries = [];
    try {
      if ((typeof FormData !== 'undefined' && body instanceof FormData) ||
        (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams)) {
        entries.push({ id: body.get('id'), quantity: body.get('quantity') });
      } else if (typeof body === 'string') {
        if (/^\s*[{[]/.test(body)) {
          var parsed = JSON.parse(body);
          entries = Array.isArray(parsed.items) ? parsed.items : [parsed];
        } else {
          var params = new URLSearchParams(body);
          entries.push({ id: params.get('id'), quantity: params.get('quantity') });
        }
      }
    } catch (e) {}
    var quantities = {};
    entries.forEach(function (entry) {
      if (!entry || entry.id == null || entry.id === '') return;
      var id = String(entry.id);
      quantities[id] = (quantities[id] || 0) + (parseInt(entry.quantity, 10) || 1);
    });
    return quantities;
  }

  if (typeof originalFetch === 'function' && !window.__klaviyoAddToCartWrapped) {
    window.__klaviyoAddToCartWrapped = true;
    window.fetch = function (input, init) {
      var promise = originalFetch.apply(this, arguments);
      try {
        var url = requestUrl(input);
        if (requestMethod(input, init) === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(url.split('#')[0])) {
          var context = lastContext && Date.now() - lastContext.time < CONTEXT_MAX_AGE ? lastContext : null;
          var quantities = requestedQuantities(init);
          promise.then(function (res) {
            if (!res || !res.ok) return;
            res.clone().json()
              .then(function (data) {
                addedItemsFrom(data).forEach(function (item) {
                  if (isShopperAdd(item)) trackAddedItem(item, context, quantities[String(item.variant_id)]);
                });
              })
              .catch(function () {});
          }).catch(function () {});
        }
      } catch (e) {}
      return promise;
    };
  }

  /* ---------- Viewed Product Localized ---------- */

  function trackViewedProductLocalized(lang, name) {
    var product = config.product;
    track('Viewed Product Localized', {
      Name: name,
      ProductID: product.id,
      Categories: product.categories,
      ImageURL: product.image,
      URL: localizeUrl(product.url, lang),
      Brand: product.brand,
      Price: product.price,
      Value: (Number(product.priceCents || 0) / 100).toFixed(2),
      CompareAtPrice: product.compareAtPrice,
      Language: lang
    });
  }

  if (config.product) {
    if (getLang() === 'de') {
      // German is the original language: nothing to wait for.
      trackViewedProductLocalized('de', config.product.name);
    } else {
      // Weglot's proxy already serves the translated page, so the DOM title is used
      // even when Weglot's script is not ready in time; Liquid title only if unreadable.
      whenWeglotReady(function () {
        trackViewedProductLocalized(getLang(), getLocalizedTitle(config.product.name, config.product.handle));
      });
    }
  }

  window.KlaviyoLang = {
    getLang: getLang,
    localizeUrl: localizeUrl,
    getLocalizedTitle: getLocalizedTitle,
    whenWeglotReady: whenWeglotReady
  };
})();
